import { Injectable } from '@angular/core';
import { collectionAdaptor, containerAdaptor, epgAdaptor, resourceAdaptor } from 'qp-adaptor';
import { MetadataService, StorefrontService as Service, createMetadataService, createStorefrontService } from 'qp-storefront';
import {
  AdaptorData,
  Airing,
  Container,
  ContainerList,
  ContainerVm,
  Resource,
  ResourceList,
  ResourceVm,
  StorefrontList,
  StorefrontVm,
} from 'qp-ui-interface';
import { getAppVersion, getDeviceType, getEndDate, getStartDate } from 'qp-utility';
import { PageType } from '../analytics/newrelic/enums/PageType';
import { NewrelicService } from '../analytics/newrelic/service/newrelic.service';
import { LocalStorageKeys } from '../shared/LocalStorageKeys';
import { EpgData } from '../shared/models/epgData';
import { ErrorEvents } from '../shared/models/ErrorEvents';
import { ErrorFactory } from '../shared/modules/errors/handler/ErrorFactory';
import { AppConfig, ConfigService } from '../shared/services/config/config.service';
import { StorefrontPolicyService } from '../shared/services/firstlight/storefront-policy/storefront-policy.service';
import { getDates } from '../shared/utils/epgUtils';
import { LocalStorageService } from '../shared/services/local-storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class StorefrontService {
  private storefrontService: Service;
  private metadataService: MetadataService;
  private readonly applicationConfig: AppConfig;

  private storefrontContentLimit;

  constructor(
    private readonly configService: ConfigService,
    private readonly policyService: StorefrontPolicyService,
    private readonly newrelic: NewrelicService,
    private readonly localStorage: LocalStorageService
  ) {
    this.applicationConfig = this.configService.applicationConfig;
    this.initialiseServices();
  }

  initialiseServices(): void {
    this.storefrontService = createStorefrontService(
      this.applicationConfig.storefrontURL,
      this.applicationConfig.catalogURL,
      undefined,
      this.policyService.getPolicy()
    );

    if (this.applicationConfig.storefrontContentLimit) {
      this.storefrontContentLimit = this.applicationConfig.storefrontContentLimit;
    } else {
      this.storefrontContentLimit = 100;
    }
    this.metadataService = createMetadataService(this.applicationConfig.vodMetaDataURL, this.policyService.getPolicy());
  }

  public async fetchResourceDetails(adaptorData: AdaptorData): Promise<ResourceVm> {
    try {
      const resource: Resource = await this.metadataService.getResourceDetails(adaptorData.resourceType, adaptorData.resourceId);
      return resourceAdaptor(
        resource as unknown as Resource,
        this.applicationConfig.imageResizeURL,
        getDeviceType(),
        this.localStorage.getItem(LocalStorageKeys.LANG),
        '0-16x9',
        adaptorData.container,
        null,
        {
          storefrontId: adaptorData?.storefrontId,
          tabId: adaptorData?.tabId,
          tabName: adaptorData?.tabName,
          originScreen: adaptorData?.originScreen,
          collectionId: adaptorData?.collectionId,
          collectionTitle: adaptorData?.collectionTitle,
          containerName: adaptorData?.containerName,
          containerId: adaptorData?.containerId,
        },
        getAppVersion()
      );
    } catch (error) {
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.RESOURCE_FETCH_ERROR));
    }
  }

  /**
   * Fetch SEO data from collection
   *
   * @param collectionId
   * @returns
   */
  public async fetchCollection(collectionId: string): Promise<any> {
    try {
      const collections: any[] = await this.storefrontService.getSEOCollections(collectionId);
      return collectionAdaptor(
        { ...collections[0], c: [] },
        this.applicationConfig.imageResizeURL,
        getDeviceType(),
        this.localStorage.getItem(LocalStorageKeys.LANG),
        0
      );
    } catch (error) {
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.COLLECTION_FETCH_ERROR));
    }
  }

  public async fetchCollectionResources(
    collectionId: string,
    pageNumber: number = 1,
    pageSize: number = 20,
    collectionTitle?: string,
    storefrontContentLimit?: number
  ): Promise<{ containers: ContainerVm[]; totalItems: number }> {
    try {
      const mc: ContainerList = await this.storefrontService.getCollectionList(collectionId, pageNumber.toString(), pageSize.toString());
      const containers = mc.containers.map((container: Container) =>
        containerAdaptor(
          container,
          this.applicationConfig.imageResizeURL,
          getDeviceType(),
          this.localStorage.getItem(LocalStorageKeys.LANG),
          null,
          null,
          {
            collectionId,
            collectionTitle,
          },
          storefrontContentLimit,
          getAppVersion()
        )
      );
      return { containers, totalItems: mc.totalContainers };
    } catch (error) {
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.COLLECTION_FETCH_ERROR));
    }
  }

  public async fetchContainers(
    tabId: string,
    url = this.applicationConfig.catalogURL,
    storefrontId = this.localStorage.getItem(LocalStorageKeys.STOREFRONTID),
    tabName?: string,
    originScreen?: string,
    pageNumber?: string,
    pageSize?: string
  ): Promise<StorefrontVm> {
    try {
      const storefrontPath = 'catalog/storefront';
      const containersResponse: ContainerList = await this.storefrontService.getContainers(
        storefrontId,
        tabId,
        storefrontPath,
        url,
        false,
        pageNumber,
        pageSize
      );

      const containers = containersResponse.containers;

      return {
        containers: containers.map((container) => {
          const containerNameLanguage = container.lon.find((l) => l.lang === 'en');
          const containerName = containerNameLanguage?.n.toLowerCase() === 'highlights' ? { n: 'Hero' } : containerNameLanguage;
          const containerId = container.id;
          return containerAdaptor(
            container,
            this.applicationConfig.imageResizeURL,
            getDeviceType(),
            this.localStorage.getItem(LocalStorageKeys.LANG),
            null,
            null,
            {
              storefrontId,
              tabId,
              tabName,
              originScreen,
              containerName: containerName.n,
              containerId,
            },
            this.storefrontContentLimit,
            getAppVersion()
          );
        }),
        totalContainers: containersResponse.totalContainers,
      };
    } catch (error) {
      this.newrelic.sendError(error, ErrorEvents.CONTAINER_FETCH_ERROR);
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.CONTAINER_FETCH_ERROR));
    }
  }

  public async fetchResources(
    ids: string[],
    name: string,
    containerName: string,
    tabId?: string,
    tabName?: string,
    originScreen?: string
  ): Promise<ContainerVm> {
    try {
      const resources: Resource[] = await this.metadataService.getResources(ids);

      const containerVm: ContainerVm = {
        id: `cvm-${name}`,
        aspectRatio: '16x9',
        name: `${name}`,
        layout: 'carousel',
        resources: resources.map((res: Resource) =>
          resourceAdaptor(
            res,
            this.applicationConfig.imageResizeURL,
            getDeviceType(),
            this.localStorage.getItem(LocalStorageKeys.LANG),
            '0-16x9',
            null,
            null,
            {
              tabId,
              tabName,
              originScreen,
              containerName,
            },
            getAppVersion()
          )
        ),
      };

      return containerVm;
    } catch (error) {
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.RESOURCE_FETCH_ERROR));
    }
  }

  async fetchUpNextContent(
    id: string,
    genres: string[],
    contentType: string,
    providerName: string,
    seasonNum?: number,
    episodeNumber?: number
  ): Promise<ResourceVm> {
    try {
      const parameters = {
        g: genres.join(','),
        cty: contentType,
        id,
        pageSize: '1',
        pn: providerName,
        snum: seasonNum,
        epnum: episodeNumber,
      };
      const response = await this.metadataService.getUpNext(parameters);
      return resourceAdaptor(
        response && response?.[0],
        this.applicationConfig.imageResizeURL,
        getDeviceType(),
        this.localStorage.getItem(LocalStorageKeys.LANG),
        '0-16x9',
        null,
        null,
        {
          collectionTitle: PageType.RelatedResource,
        },
        getAppVersion()
      );
    } catch (error) {
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.UPNEXT_FETCH_ERROR));
    }
  }

  public async fetchSeries(
    resourceId: string,
    pageNumber: number = 1,
    pageSize: number = 30,
    path: string = '',
    seriesPath: string = ''
  ): Promise<ResourceVm[]> {
    try {
      const { seasonsSort, seasonsSortBy } = this.configService.applicationConfig;
      const response = await this.metadataService.getSeries(resourceId, pageNumber, pageSize, path, seriesPath, seasonsSortBy, seasonsSort);
      const resources: Resource[] = response?.resources;
      return resources.map((res: Resource) =>
        resourceAdaptor(
          res,
          this.applicationConfig.imageResizeURL,
          getDeviceType(),
          this.localStorage.getItem(LocalStorageKeys.LANG),
          '0-16x9'
        )
      );
    } catch (error) {
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.SERIES_FETCH_ERROR));
    }
  }

  public async fetchEpisodes(
    resourceId: string,
    seasonId: string,
    pageNumber: string,
    pageSize: string,
    episodesData: { seriesPath: string; episodesPath: string; seasonNumber?: string },
    storefrontId?: string,
    customData?: { tabId: string; tabName: string; originScreen?: string }
  ): Promise<ContainerVm> {
    try {
      pageNumber = pageNumber ? pageNumber : '1';
      pageSize = pageSize ? pageSize : '20';
      if (!customData) {
        customData = {
          tabId: '',
          tabName: '',
          originScreen: '',
        };
      }
      const { tabId, tabName, originScreen } = customData;
      const { seriesPath, episodesPath, seasonNumber } = episodesData;
      const { episodesSort, episodesSortBy } = this.configService.applicationConfig;
      const response = await this.metadataService.getEpisodes(
        resourceId,
        seasonId,
        pageNumber,
        pageSize,
        seriesPath,
        episodesPath,
        episodesSortBy,
        episodesSort
      );
      const resources: Resource[] = response?.resources;
      const containerVm: ContainerVm = {
        id: resourceId,
        aspectRatio: '16x9',
        name: `Season ${seasonNumber}`,
        layout: 'carousel',
        resources: resources.map((res: Resource) =>
          resourceAdaptor(
            res,
            this.applicationConfig.imageResizeURL,
            getDeviceType(),
            this.localStorage.getItem(LocalStorageKeys.LANG),
            '0-16x9',
            null,
            null,
            {
              storefrontId,
              tabId,
              tabName,
              originScreen,
            },
            getAppVersion()
          )
        ),
      };

      containerVm.totalItems = response.totalResources;

      return containerVm;
    } catch (error) {
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.EPISODES_FETCH_ERROR));
    }
  }

  public async fetchViewMore(
    viewMoreUrl: string[],
    container: ContainerVm,
    pageNumber: number = 1,
    pageSize: number = 10,
    customData?: { tabId: string; tabName: string; originScreen?: string; collectionTitle?: string }
  ): Promise<ContainerVm[]> {
    try {
      if (!customData) {
        customData = {
          tabId: '',
          tabName: '',
          originScreen: '',
          collectionTitle: '',
        };
      }
      const { tabId, tabName, originScreen, collectionTitle } = customData;
      const results: ResourceList = await this.metadataService.getViewMore(viewMoreUrl, pageNumber, pageSize);
      const allResources = results?.resources?.map((resource) =>
        resourceAdaptor(
          resource,
          this.applicationConfig.imageResizeURL,
          getDeviceType(),
          this.localStorage.getItem(LocalStorageKeys.LANG),
          container?.aspectRatio ? '0-' + container.aspectRatio : '0-2x3',
          null,
          null,
          {
            tabId,
            tabName,
            originScreen,
            collectionTitle,
          },
          getAppVersion()
        )
      );
      if (pageNumber !== 1) {
        container.resources.push(...allResources);
      } else {
        container.resources = allResources;
      }
      return [container];
    } catch (error) {
      this.newrelic.sendError(error, ErrorEvents.COLLECTION_FETCH_ERROR);
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.COLLECTION_FETCH_ERROR));
    }
  }

  public async getStorefrontList(): Promise<StorefrontList> {
    return this.storefrontService.getStorefrontList();
  }

  /**
   * Fetch EPG page
   *
   * @param pageNumber
   * @param pageSize
   * @returns
   */
  public async fetchEpgPage(pageNumber: number, pageSize: number, initDate?: string) {
    return this.fetchEpgData(
      {
        locale: this.policyService.getLocale(),
        pf: this.policyService.getProfile(),
        pageNumber,
        pageSize,
      },
      initDate
    );
  }

  /**
   * Fetch epg data
   *
   * @param startDate
   * @param endDate
   * @returns EpgChannelVm[]
   */
  public async fetchEpgData(params = {}, initDate?: string): Promise<EpgData> {
    try {
      const startDate = initDate ?? getStartDate();
      const endDate = getEndDate(startDate);
      const response = await this.storefrontService.getEpg(startDate, endDate, params);
      const channels = epgAdaptor(
        response.channels,
        startDate,
        endDate,
        this.configService.applicationConfig.imageResizeURL,
        getDeviceType(),
        this.localStorage.getItem(LocalStorageKeys.LANG)
      );
      const dates = getDates(3, 3, initDate);
      return { channels, dates, startDate, endDate, channelsCount: response.channelsCount };
    } catch (error) {
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.RESOURCE_FETCH_ERROR));
    }
  }

  /**
   * Fetch ongoing carousel
   * Includes tabs
   *
   * @returns tabsVm and storefrontId
   */
  async fetchOngoing(): Promise<ResourceVm[]> {
    try {
      const response = await this.metadataService.getOngoingDetails();
      const resources = response;

      return resources.map((res) =>
        resourceAdaptor(
          res,
          this.configService.applicationConfig.imageResizeURL,
          getDeviceType(),
          this.localStorage.getItem(LocalStorageKeys.LANG),
          '0-16x9',
          undefined,
          undefined,
          undefined,
          undefined,
          res as unknown as Airing
        )
      );
    } catch (error) {
      return Promise.reject(ErrorFactory.get(error, ErrorEvents.CONTAINER_FETCH_ERROR));
    }
  }
}
